/*
 * Algolia data source for the theme's native predictive search.
 * The theme's `predictive-search` component (assets/search.js) keeps handling
 * open/close, overlay, spinner, caching and clearing. This file only replaces
 * its renderSection() so results come from Algolia instead of Shopify's
 * /search/suggest endpoint (which is capped for very large catalogs).
 */
(function () {
  var cfg = window.AlgoliaConfig;
  if (!cfg || !cfg.appId || !cfg.apiKey || !('customElements' in window)) return;

  var endpoint =
    'https://' + cfg.appId + '-dsn.algolia.net/1/indexes/' +
    encodeURIComponent(cfg.index) + '/query';

  // '' on the primary locale, '/ar' on Arabic — without it an Arabic shopper
  // picking a suggestion is thrown back to the English product page.
  var ROOT = String(cfg.rootUrl || '/').replace(/\/+$/, '');

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function money(amount) {
    var n = Number(amount) || 0;
    var formatted = n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    var fmt = cfg.moneyFormat || '';
    if (fmt.indexOf('{{') !== -1) {
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/g, formatted)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatted)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(n).toLocaleString())
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(n).toLocaleString());
    }
    return (cfg.currencySymbol || '') + formatted;
  }

  function productCard(hit) {
    var url = ROOT + '/products/' + encodeURIComponent(hit.handle || '');
    var img = hit.product_image || hit.image;
    var mediaContain = cfg.imageFill ? '' : ' media--contain';
    var imgHtml = img
      ? '<img src="' + esc(img) + '" alt="' + esc(hit.title) +
        '" loading="lazy" width="96" height="96" />'
      : '';
    var vendorHtml = (cfg.showVendor && hit.vendor)
      ? '<p class="text-xs text-opacity">' + esc(hit.vendor) + '</p>'
      : '';
    var price = hit.price || hit.variants_min_price || 0;
    var priceHtml = (cfg.showPrice && Number(price) > 0)
      ? '<div class="flex flex-col gap-1">' +
          '<div class="flex flex-wrap items-end gap-2 text-sm">' +
            '<span class="price">' + esc(money(price)) + '</span>' +
          '</div>' +
        '</div>'
      : '';

    return '' +
      '<div class="horizontal-product flex items-center gap-4 md:gap-6 w-full">' +
        '<a class="horizontal-product__media media media--' + esc(cfg.imageRatio || 'square') +
          mediaContain + ' relative overflow-hidden shrink-0" href="' + esc(url) + '">' +
          imgHtml +
        '</a>' +
        '<div class="horizontal-product__details grow flex flex-col justify-start gap-1d5">' +
          '<div class="flex flex-col gap-1"><div class="block">' +
            vendorHtml +
            '<a href="' + esc(url) + '" class="horizontal-product__title reversed-link font-medium text-base leading-tight">' +
              esc(hit.title) +
            '</a>' +
          '</div></div>' +
          priceHtml +
        '</div>' +
      '</div>';
  }

  function resultsHtml(hits, query) {
    if (!hits.length) {
      var msg = (cfg.strings && cfg.strings.noResults ? cfg.strings.noResults : 'No results for "%TERMS%"')
        .split('%TERMS%').join(esc(query));
      var hint = (cfg.strings && cfg.strings.differentTerms) || '';
      return '<div class="search__empty flex justify-center items-star text-center w-full h-full">' +
        '<div class="drawer__empty flex flex-col gap-5 md:gap-8">' +
          '<p class="drawer__empty-text h2 heading leading-none tracking-tight">' + msg + '.</p>' +
          '<p class="drawer__empty-message text-sm md:text-base leading-tight">' + esc(hint) + '</p>' +
        '</div></div>';
    }
    var heading = (cfg.strings && cfg.strings.products) || 'Products';
    var viewMore = (cfg.strings && cfg.strings.viewMore) || 'View all results';
    return '' +
      '<div class="grid gap-3 md:gap-4 w-full">' +
        '<p class="search__heading block text-xs uppercase leading-tight tracking-widest">' +
          esc(heading) +
        '</p>' +
        '<div class="horizontal-products grid">' + hits.map(productCard).join('') + '</div>' +
      '</div>' +
      '<div class="drawer__sticky z-1 sticky left-0 bottom-0 w-full">' +
        '<button class="button button--primary icon-with-text w-full" type="submit" is="hover-button">' +
          '<span class="btn-fill" data-fill></span>' +
          '<span class="btn-text">' + esc(viewMore) + '</span>' +
        '</button>' +
      '</div>';
  }

  customElements.whenDefined('predictive-search').then(function () {
    var proto = customElements.get('predictive-search').prototype;

    proto.renderSection = function (url) {
      var self = this;
      var query;
      try {
        query = new URL(url, window.location.origin).searchParams.get('q') || '';
      } catch (e) {
        query = this.getQuery();
      }
      var cacheKey = 'algolia:' + query;

      var inject = function (html) {
        var id = 'PredictiveSearchResults-' + theme.utils.sectionId(self);
        var container = document.getElementById(id);
        if (container) container.innerHTML = html;
      };

      if (this.cachedMap.has(cacheKey)) {
        inject(this.cachedMap.get(cacheKey));
        this.setAttribute('results', '');
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.setAttribute('loading', '');

      fetch(endpoint, {
        method: 'POST',
        signal: this.abortController.signal,
        headers: {
          'Content-Type': 'application/json',
          'X-Algolia-Application-Id': cfg.appId,
          'X-Algolia-API-Key': cfg.apiKey
        },
        body: JSON.stringify({ query: query, hitsPerPage: cfg.autocompleteLimit || 6 })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var html = resultsHtml((data && data.hits) || [], query);
          self.cachedMap.set(cacheKey, html);
          inject(html);
          self.removeAttribute('loading');
          self.setAttribute('results', '');
        })
        .catch(function (error) {
          if (error && error.name !== 'AbortError') {
            self.removeAttribute('loading');
            console.error('[Algolia search]', error);
          }
        });
    };
  });
})();
